import React, { useEffect, useState } from 'react';
import './TriStateChecklist.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faChevronDown, faChevronUp, faMinus, faSpinner, faTimes, faTrashArrowUp } from '@fortawesome/free-solid-svg-icons';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { NextStep } from '../../../amplify/appsync/query';
import { callWrite, UPDATE_NEXT_STEP } from '../../../amplify/appsync/write';
import NotificationToast from '../../common/NotificationToast';

type StepState = 'UNCHECKED' | 'IN_PROGRESS' | 'COMPLETED';

interface ChecklistItem {
    action: string;
    priority: 'HIGH' | 'MEDIUM' | 'LOW';
    rationale: string;
    previous_id: string;
}

interface ChecklistCategory {
    category: string;
    items: ChecklistItem[];
}

interface Props {
    data: ChecklistCategory[];
    existingNextSteps: NextStep[];
    setExistingNextSteps: (steps: NextStep[]) => void;
}

function splitNextStepsByLatestUpdate(nextSteps: NextStep[]): {
    latest: NextStep[],
    archived: NextStep[]
} {
    if (nextSteps.length === 0) {
        return { latest: [], archived: [] };
    }

    // Split into latest and archived
    const latest: NextStep[] = [];
    const archived: NextStep[] = [];

    for (const step of nextSteps) {
        if (step.status !== "DELETED") {
            latest.push(step);
        } else {
            archived.push(step);
        }
    }

    return { latest, archived };
}

const TriStateChecklist: React.FC<Props> = ({ data, existingNextSteps, setExistingNextSteps }) => {
    const [states, setStates] = useState<Record<string, StepState>>({});
    const [latestNextSteps, setLatestNextSteps] = useState<NextStep[]>([]);
    const [archivedNextSteps, setArchivedNextSteps] = useState<NextStep[]>([]);
    const [isLoading, setIsLoading] = useState<Record<string, boolean>>({});
    const [isDeleting, setIsDeleting] = useState<Record<string, boolean>>({});
    const [showArchived, setShowArchived] = useState(false);
    const [hoveredItem, setHoveredItem] = useState<string | null>(null);
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState("");

    useEffect(() => {
        if (existingNextSteps) {
            const { latest, archived } = splitNextStepsByLatestUpdate(existingNextSteps);
            setLatestNextSteps(latest);
            setArchivedNextSteps(archived);
        }
    }, [existingNextSteps]);

    useEffect(() => {
        const initStates: Record<string, StepState> = {};
        latestNextSteps.forEach(step => {
            initStates[step.id.toString()] = step.status as StepState;
        });
        setStates(initStates);
    }, [latestNextSteps]);

    const getNextState = (state: string) => {
        if (state === "UNCHECKED") {
            return "In Progress";
        } else if (state === "IN_PROGRESS") {
            return "Completed";
        } else {
            return "Not Started"
        }
    }

    function groupByCategory(steps: NextStep[]): Record<string, NextStep[]> {
        return steps.reduce((acc, step) => {
            if (!acc[step.category]) acc[step.category] = [];
            acc[step.category].push(step);
            return acc;
        }, {} as Record<string, NextStep[]>);
    }

    const toggleState = (key: string) => {
        const current = states[key] || 'UNCHECKED';
        const next: StepState =
            current === 'UNCHECKED' ? 'IN_PROGRESS' : current === 'IN_PROGRESS' ? 'COMPLETED' : 'UNCHECKED';

        setIsLoading(d => {
            return {
                ...d,
                [key]: true,
            }
        });
        callWrite({
            operation: UPDATE_NEXT_STEP,
            id: key,
            status: next,
        })
        .then((output: any) => {
            setStates(prev => ({
                ...prev,
                [key]: next,
            }));
            setIsLoading(prev => ({ ...prev, [key]: false }));
        });
    };

    const toggleDeleteNextStep = (key: string) => {
        setIsDeleting(d => {
            return {
                ...d,
                [key]: true,
            }
        });
        let isDeleted = false;
        for (let step of existingNextSteps) {
            if (step.id === Number(key)) {
                isDeleted = step.status === "DELETED";
            }
        }
        const newStatus = isDeleted ? "UNCHECKED" : "DELETED";
        callWrite({
            operation: UPDATE_NEXT_STEP,
            id: key,
            status: newStatus,
        })
        .then((output: any) => {
            const newNextSteps = [];
            for (let step of existingNextSteps) {
                const newStep = {
                    ...step,
                };
                if (step.id === Number(key)) {
                    newStep.status = newStatus;
                }
                newNextSteps.push(newStep);
            }
            setExistingNextSteps(newNextSteps);
            setIsDeleting(d => {
                return {
                    ...d,
                    [key]: false,
                }
            });
            if (isDeleted) {
                setToastMessage("Restored Item");
            } else {
                setToastMessage("Archived Item");
            }
            setShowToast(true);
        });
    };

    const handleDeleteClick = (key: string, e: React.MouseEvent) => {
        e.stopPropagation(); // Prevent triggering the checkbox toggle
        toggleDeleteNextStep(key);
    };

    const renderCheckboxIcon = (state: StepState) => {
        switch (state) {
            case 'IN_PROGRESS':
                return <FontAwesomeIcon icon={faMinus} />;
            case 'COMPLETED':
                return <FontAwesomeIcon icon={faCheck} />;
            default:
                return null;
        }
    };

    const getCheckboxClass = (state: StepState) => {
        return `checkbox ${state ? state.toLowerCase().replaceAll("_", "") : ""}`;
    };

    const getPriorityClass = (priority: string) => {
        return `priority-badge ${priority ? priority.toLowerCase() : ""} me-1`;
    };

    const renderChecklistItem = (item: NextStep, isArchived = false) => {
        const key = item.id.toString();
        const state = states[key] || 'UNCHECKED';

        return (
            <div 
                key={key} 
                className="checklist-item"
                onMouseEnter={() => setHoveredItem(key)}
                onMouseLeave={() => setHoveredItem(null)}
                style={{ position: 'relative' }}
            >
                <OverlayTrigger
                    placement="bottom"
                    overlay={<Tooltip>Mark {getNextState(state)}</Tooltip>}
                >
                    {isLoading[key] ? (
                        <div className="checklist-loading">
                            <FontAwesomeIcon icon={faSpinner} spin />
                        </div>
                    ) : !isArchived ? (
                        <div
                            className={getCheckboxClass(state)}
                            onClick={() => toggleState(key)}
                            title={item.action}
                            style={{ cursor: 'pointer' }}
                        >
                            {renderCheckboxIcon(state)}
                        </div>
                    ) : (
                        <></>
                    )}
                </OverlayTrigger>
                <div className="checklist-content">
                    <OverlayTrigger
                        placement="bottom"
                        overlay={<Tooltip>{item.priority} Priority</Tooltip>}
                    >
                        <span className={getPriorityClass(item.priority)}>{item.priority ? item.priority[0] : "-"}</span>
                    </OverlayTrigger>
                    <span>
                        <span>{item.action}</span>
                        <span className="text-muted">&bull; {item.rationale}</span>
                    </span>
                </div>
                {hoveredItem === key && (
                    <OverlayTrigger
                        placement="bottom"
                        overlay={item.status === "DELETED" ? <Tooltip>Restore item</Tooltip> : <Tooltip>Delete item</Tooltip>}
                    >
                        <button
                            className="checklist-delete-btn"
                            onClick={(e) => handleDeleteClick(key, e)}
                            disabled={isDeleting[key]}
                        >
                            {isDeleting[key] ? (
                                <FontAwesomeIcon icon={faSpinner} spin />
                            ) : (
                                <FontAwesomeIcon icon={item.status === "DELETED" ? faTrashArrowUp : faTimes} />
                            )}
                        </button>
                    </OverlayTrigger>
                )}
            </div>
        );
    };

    return (
        <>
            <div className="checklist-section">
                {Object.entries(groupByCategory(latestNextSteps)).map(([category, items]) => (
                    <div key={category}>
                        <strong>{category}</strong>
                        {items.map(item => renderChecklistItem(item))}
                    </div>
                ))}
            </div>

            <div className="checklist-section checklist-archived rounded">
                <div className="checklist-archived-header" onClick={() => setShowArchived(!showArchived)}>
                    <strong>Archived Tasks {showArchived ? <FontAwesomeIcon icon={faChevronUp} /> : <FontAwesomeIcon icon={faChevronDown} />}</strong>
                </div>
                {showArchived && (
                    <div className="mt-2">
                        {
                            Object.entries(groupByCategory(archivedNextSteps)).map(([category, items]) => (
                                <div key={category} className="checklist-archived-item">
                                    <strong>{category}</strong>
                                    {items.map(item => renderChecklistItem(item, true))}
                                </div>
                            ))
                        }
                    </div>
                )}
            </div>
            <NotificationToast
                show={showToast}
                onClose={() => setShowToast(false)}
                message={toastMessage}
                variant="success"
                delay={4000}
            />
        </>
    );
};

export default TriStateChecklist;
